import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import withConfig from 'config/withConfig';
import Button from '@spothero/ui/button';
import SpotListItemPlaceholder from './SpotListItemPlaceholder';
import SpotListNoResults from './SpotListNoResults';
import {updateSelected} from 'spot/spot-actions';
import {cancelSpotRequests} from 'spots/spots-actions';
import {resetFilters} from 'filters/filters-actions';

const renderPlaceholders = (count = 5) => {
    const placeholders = [];
    let i = 0;

    for (i; i < count; i++) {
        placeholders.push(<SpotListItemPlaceholder key={i} />);
    }

    return placeholders;
};

export const SpotList = ({
    isMobile,
    canonicalUrl,
    spots,
    numActiveFilters,
    validFrom,
    validThrough,
    showListLoader,
    showLoadingComplete,
    loadNextPage,
    onResetFiltersClick,
    updateSelectedSpot,
    cancelAllSpotRequests,
    isRebookingReservation,
    isAdmin,
    sortBy,
    selectedSpotId,
    isLowInventoryEnabled,
    isSoldOutEnabled,
    showNoSpotsFound,
    onShowDetailsClick,
    VerticalPromo,
    SpotListItem,
}) => {
    return (
        <div
            className="SpotList"
            itemProp="offers"
            itemScope
            itemType="http://schema.org/AggregateOffer"
        >
            {canonicalUrl && <meta itemProp="url" content={canonicalUrl} />}
            {spots.length > 0 && (
                <>
                    <meta itemProp="validFrom" content={validFrom} />
                    <meta itemProp="validThrough" content={validThrough} />
                </>
            )}
            <meta itemProp="offerCount" content={spots.length} />
            <div className="SpotList-spots">
                {VerticalPromo}
                {!isMobile &&
                    !showNoSpotsFound &&
                    spots.length < 1 &&
                    renderPlaceholders()}
                {showNoSpotsFound && (
                    <SpotListNoResults
                        numFilters={numActiveFilters}
                        onResetFiltersClick={onResetFiltersClick}
                    />
                )}
                {spots.map(spot => {
                    const isActive = spot.spotId === selectedSpotId;

                    return (
                        <SpotListItem
                            key={`${spot.spotId}-${isActive}`}
                            spot={spot}
                            isMobile={isMobile}
                            isActive={isActive}
                            isRebookingReservation={isRebookingReservation}
                            isAdmin={isAdmin}
                            updateSelectedSpot={updateSelectedSpot}
                            cancelAllSpotRequests={cancelAllSpotRequests}
                            isLowInventoryEnabled={isLowInventoryEnabled}
                            isSoldOutEnabled={isSoldOutEnabled}
                            onShowDetailsClick={onShowDetailsClick}
                        />
                    );
                })}
            </div>
            {isMobile && (
                <>
                    {!showLoadingComplete &&
                        !showNoSpotsFound &&
                        (!spots || spots.length === 0) &&
                        renderPlaceholders()}
                    {!showLoadingComplete && spots && spots.length > 0 && (
                        <div className="SpotList-additional-actions">
                            <Button
                                color="primary"
                                loading={showListLoader}
                                onClick={loadNextPage}
                            >
                                {sortBy === 'price'
                                    ? 'Load More and Re-Sort'
                                    : 'Load More Spots'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

SpotList.propTypes = {
    isMobile: PropTypes.bool.isRequired,
    numActiveFilters: PropTypes.number,
    showListLoader: PropTypes.bool,
    showLoadingComplete: PropTypes.bool,
    validFrom: PropTypes.string,
    validThrough: PropTypes.string,
    spots: PropTypes.arrayOf(PropTypes.object),
    canonicalUrl: PropTypes.string,
    loadNextPage: PropTypes.func,
    onResetFiltersClick: PropTypes.func,
    cancelAllSpotRequests: PropTypes.func.isRequired,
    updateSelectedSpot: PropTypes.func.isRequired,
    isRebookingReservation: PropTypes.bool,
    isAdmin: PropTypes.bool,
    sortBy: PropTypes.string.isRequired,
    selectedSpotId: PropTypes.number,
    isLowInventoryEnabled: PropTypes.bool,
    isSoldOutEnabled: PropTypes.bool,
    showNoSpotsFound: PropTypes.bool,
    onShowDetailsClick: PropTypes.func,
    VerticalPromo: PropTypes.node,
    SpotListItem: PropTypes.func,
};

const mapStateToProps = state => {
    const {
        search: {
            data: {sortBy, showNoSpotsFound},
        },
        checkout: {isRebookingReservation},
        filters: {numActive: numActiveFilters},
        user: {
            data: {isAdmin},
        },
        spot: {selected: selectedSpot},
    } = state;

    return {
        numActiveFilters,
        sortBy,
        isRebookingReservation,
        isAdmin,
        selectedSpot,
        showNoSpotsFound,
    };
};

const mapDispatchToProps = {
    onResetFiltersClick: resetFilters,
    cancelAllSpotRequests: cancelSpotRequests,
    updateSelectedSpot: updateSelected,
};

const enhance = compose(
    withConfig(['isMobile']),
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotList);
